import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Award, ShieldCheck, Heart, Sparkles, CheckCircle, MapPin } from 'lucide-react';
import { fetchTreatments, fetchPages, getSlug } from '../lib/api';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function Home() {
  const [treatments, setTreatments] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [allTreatments, allPages] = await Promise.all([
          fetchTreatments(),
          fetchPages()
        ]);
        setTreatments(allTreatments);
        const indexPage = allPages.find(p => p.page_slug === 'index');
        if (indexPage) {
          setPageData(indexPage);
        }
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Filter main treatments vs seasonal
  const mainTreatments = treatments.filter(t => !t.is_seasonal);
  const seasonalTreatments = treatments.filter(t => t.is_seasonal);

  return (
    <>
      <Helmet>
        <title>Velkommen til Klinik Acu-sense | Akupunktur i Hammel</title>
        <meta name="description" content="Klinik Acu-sense i Hammel tilbyder akupunktur, Seizen Hari, øreakupunktur, Auriculo og posturologi ved RAB-godkendt akupunktør og neurofysioterapeut Brian Hangaard. Nem online booking." />
        <meta property="og:title" content="Velkommen til Klinik Acu-sense | Akupunktur i Hammel" />
        <meta property="og:description" content="Klinik Acu-sense i Hammel tilbyder akupunktur, Seizen Hari, øreakupunktur, Auriculo og posturologi ved RAB-godkendt akupunktør og neurofysioterapeut Brian Hangaard. Nem online booking." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/91ef7221-010c-4281-b8d0-6e03f8b95382.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/91ef7221-010c-4281-b8d0-6e03f8b95382.webp" />
        <link rel="canonical" href="https://www.acu-sense.dk/" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#F3F1EC] to-[#FAF9F6] pt-12 pb-20 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8FA89B]/10 text-[#3D4F44] text-xs font-semibold tracking-wide uppercase">
                  <Award className="w-4 h-4 text-[#5F7464]" />
                  RAB-godkendt akupunktør
                </div>
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#5F7464]/15 border border-[#5F7464]/20 text-[#2C3531] text-xs font-semibold">
                  <MapPin className="w-3.5 h-3.5 text-[#5F7464]" />
                  Ny adresse pr. 1. sep. 2026: Banealle 8F, Hammel
                </div>
              </div>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C3531] leading-tight">
                Akupunktur i Hammel med fokus på <span className="text-[#5F7464] italic font-normal">ro & faglighed</span>
              </h1>

              <p className="text-base sm:text-lg text-[#5C6360] leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Klinik Acu-sense tilbyder individuelt tilrettelagte behandlinger, der genskaber balancen og energien i din krop. Brian Hangaard kombinerer mange års erfaring med en stærk sundhedsfaglig baggrund.
              </p>

              {/* Badges */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white border border-[#E6E4DF] shadow-sm">
                  <div className="w-6 h-6 rounded-full bg-[#8FA89B]/20 flex items-center justify-center text-[#5F7464]">
                    <CheckCircle className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-semibold text-[#2C3531]">Brug din sundhedsforsikring</span>
                </div>
                <a
                  href={BOOKING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#5F7464]/10 border border-[#5F7464]/20 shadow-sm hover:bg-[#5F7464]/15 transition-colors duration-200 group"
                >
                  <div className="w-6 h-6 rounded-full bg-[#5F7464] flex items-center justify-center text-white">
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-xs font-semibold text-[#3D4F44] group-hover:text-[#2C3531]">
                    Mulighed for gratis 30 min. undersøgelse &rarr;
                  </span>
                </a>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4">
                <a
                  href={BOOKING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book tid online
                </a>
                <Link
                  to="/behandlinger"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-white border border-[#D1CFC9] text-[#2C3531] font-medium tracking-wide hover:bg-[#F3F1EC] transition-colors duration-200"
                >
                  Se behandlinger
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                {/* Decorative background shape */}
                <div className="absolute -inset-4 rounded-3xl bg-[#E6E4DF] transform rotate-3 scale-95 -z-10" />
                <div className="absolute -inset-4 rounded-3xl bg-[#8FA89B]/10 transform -rotate-2 scale-98 -z-10" />
                
                <img
                  src="https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/91ef7221-010c-4281-b8d0-6e03f8b95382.webp"
                  alt="Brian Hangaard - Klinik Acu-sense"
                  className="w-full h-[480px] object-cover rounded-2xl shadow-xl border-4 border-white"
                />
                
                {/* Float badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg border border-[#E6E4DF] max-w-[200px] hidden sm:block">
                  <div className="flex items-center gap-2 text-[#5F7464] mb-1">
                    <ShieldCheck className="w-5 h-5 shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wider">RAB Godkendt</span>
                  </div>
                  <p className="text-[11px] text-[#5C6360] leading-snug">
                    Din garanti for professionel og godkendt behandling.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white border-b border-[#E6E4DF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="w-12 h-12 rounded-full bg-[#F3F1EC] flex items-center justify-center mx-auto text-[#5F7464]">
            <Heart className="w-6 h-6" />
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C3531] tracking-tight">
            Mød din behandler, Brian Hangaard
          </h2>
          
          <p className="text-lg sm:text-xl text-[#4A504D] leading-relaxed font-serif italic">
            “Klinik Acu-sense i Hammel tilbyder akupunktur med fokus på ro, faglighed og individuel behandling. Brian har mange års erfaring og er RAB-godkendt akupunktør samt uddannet neurofysioterapeut. Når det giver mening, kombineres akupunktur med neurofysioterapeutisk viden for at skabe en målrettet behandling til den enkelte.”
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 text-left">
            <div className="bg-[#FAF9F6] p-6 rounded-2xl border border-[#E6E4DF]">
              <h3 className="font-serif text-lg font-bold text-[#2C3531] mb-3">
                Sundhedsfaglig synergi
              </h3>
              <p className="text-sm text-[#5C6360] leading-relaxed">
                Som både uddannet akupunktør og neurofysioterapeut har Brian en dybdegående forståelse for kroppens anatomi og nervesystem. Ved at kombinere den østlige akupunkturs helhedsorienterede tilgang med vestlig neurofysioterapeutisk viden, skabes en unik og yderst målrettet behandling.
              </p>
            </div>
            <div className="bg-[#FAF9F6] p-6 rounded-2xl border border-[#E6E4DF]">
              <h3 className="font-serif text-lg font-bold text-[#2C3531] mb-3">
                Behandling af årsagen
              </h3>
              <p className="text-sm text-[#5C6360] leading-relaxed">
                Målet er ikke kun at lindre dine umiddelbare symptomer, men at finde og behandle den dybere årsag til dine ubalancer. Uanset om du døjer med smerter, migræne, søvnproblemer eller stress, tilrettelægges forløbet helt individuelt efter din krops behov.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Highlights Section */}
      {seasonalTreatments.length > 0 && (
        <section className="py-20 bg-[#F3F1EC] border-b border-[#E6E4DF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#5F7464]/10 text-[#3D4F44] text-xs font-bold tracking-wider uppercase">
                <Sparkles className="w-3.5 h-3.5" />
                Aktuelt lige nu
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C3531] tracking-tight">
                Sæsonrelevante behandlinger
              </h2>
              <p className="text-base text-[#5C6360]">
                Disse behandlinger er særligt aktuelle og effektive i øjeblikket. Book i god tid for at sikre dig den bedste effekt.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {seasonalTreatments.map((treatment) => (
                <div
                  key={treatment.id || treatment.slug}
                  className="bg-white rounded-2xl border border-[#E6E4DF] overflow-hidden shadow-sm flex flex-col"
                >
                  <div className="h-48 relative overflow-hidden">
                    <img
                      src={treatment.image?.[0] || "https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/3c35f7cd-14a5-431c-9e61-3582fad6e704.webp"}
                      alt={treatment.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-[#5F7464] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      Sæsonbehandling
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-serif text-xl font-bold text-[#2C3531]">
                        {treatment.title}
                      </h3>
                      <div
                        className="text-sm text-[#5C6360] line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: treatment.description }}
                      />
                    </div>
                    <div className="pt-4 border-t border-[#F3F1EC] flex items-center justify-between gap-4">
                      <span className="text-xs font-bold text-[#5F7464] uppercase tracking-wider">
                        {treatment.price}
                      </span>
                      <Link
                        to={`/behandlinger/${getSlug(treatment)}`}
                        className="inline-flex items-center text-sm font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200 whitespace-nowrap"
                      >
                        Læs mere
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Treatments Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C3531] tracking-tight">
              Mine behandlingsformer
            </h2>
            <p className="text-base text-[#5C6360]">
              Klinikken anvender en række specialiserede og anerkendte behandlingsformer for at give dig den bedst mulige og mest målrettede hjælp.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mainTreatments.map((treatment) => (
              <div
                key={treatment.id || treatment.slug}
                className="bg-[#FAF9F6] rounded-2xl border border-[#E6E4DF] overflow-hidden flex flex-col justify-between"
              >
                <div>
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={treatment.image?.[0] || "https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/c9b695e4-28b7-4bf4-8745-14a1efd7932e.webp"}
                      alt={treatment.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="font-serif text-xl font-bold text-[#2C3531]">
                      {treatment.title}
                    </h3>
                    <div
                      className="text-sm text-[#5C6360] line-clamp-4 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: treatment.description }}
                    />
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <div className="pt-4 border-t border-[#E6E4DF] flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#5F7464]">
                      {treatment.title === 'Posturologi' ? 'Holdningskorrektion' : 'Individuel terapi'}
                    </span>
                    <Link
                      to={`/behandlinger/${getSlug(treatment)}`}
                      className="inline-flex items-center text-sm font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200 whitespace-nowrap"
                    >
                      Læs mere
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subsidies & Insurance Section */}
      <section className="py-16 bg-[#FAF9F6] border-t border-b border-[#E6E4DF]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-[#E6E4DF] p-8 md:p-12 shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#5F7464] uppercase tracking-wider">
                <ShieldCheck className="w-5 h-5" />
                Økonomisk tilskud og dækning
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531] tracking-tight">
                Få tilskud fra Sygeforsikringen Danmark
              </h2>
              <p className="text-sm sm:text-base text-[#5C6360] leading-relaxed">
                Da Brian Hangaard er uddannet og <strong>RAB-godkendt akupunktør</strong>, yder Sygeforsikringen Danmark tilskud til dine akupunkturbehandlinger. Derudover dækker langt de fleste private sundhedsforsikringer samt arbejdspladsers sundhedsordninger udgifterne til behandlingen helt eller delvist.
              </p>
              <p className="text-xs text-[#8A908D]">
                * Jeg anbefaler altid, at du kontakter dit eget forsikringsselskab eller undersøger dine personlige tilskudsmuligheder hos Danmark inden din første behandling.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4 justify-center items-center lg:items-end w-full">
              <div className="w-full max-w-[240px] p-5 rounded-2xl bg-[#F3F1EC] border border-[#E6E4DF] text-center space-y-1">
                <span className="text-[10px] uppercase tracking-widest text-[#5F7464] font-bold">Sygeforsikringen</span>
                <div className="font-serif text-2xl font-black text-[#2C3531] tracking-tight">Danmark</div>
                <span className="text-xs text-[#5C6360] block pt-1">Godkendt tilskud</span>
              </div>
              <a
                href={BOOKING_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full max-w-[240px] inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-[#5F7464] text-white text-sm font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book tid online
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
